import { useMemo } from 'react'

import { useSubscription } from '@apollo/client'
import isNil from 'lodash.isnil'
import omit from 'lodash.omit'
import {
  ArchiveRecordTemplateMutation,
  ArchiveRecordTemplateMutationVariables,
  CreateRecordTemplateMutation,
  CreateRecordTemplateMutationVariables,
  DuplicateRecordTemplateMutation,
  DuplicateRecordTemplateMutationVariables,
  GetRecordTaskTemplateGroupOptionsForRecordTemplateQuery,
  GetRecordTaskTemplateGroupOptionsForRecordTemplateQueryVariables,
  GetRecordTemplatesQuery,
  GetRecordTemplatesQueryVariables,
  LinkFieldGroupOnRecordSettingsToRecordTemplateMutation,
  LinkFieldGroupOnRecordSettingsToRecordTemplateMutationVariables,
  RecordTemplateQuery,
  RecordTemplateQueryVariables,
  RecordTemplatesInput,
  RecordTemplatesUserHasAccessToQuery,
  RecordTemplatesUserHasAccessToQueryVariables,
  ActivateRecordTemplateMutation,
  ActivateRecordTemplateMutationVariables,
  FillRecordTemplateApplyFormMutation,
  FillRecordTemplateApplyFormMutationVariables,
  FillFormFieldsFromGISMutation,
  FillFormFieldsFromGISMutationVariables,
  UpdateRecordTemplateMutation,
  UpdateRecordTemplateMutationVariables,
  WorkflowTemplatesQuery,
  WorkflowTemplatesQueryVariables,
  FillRecordTemplateApplyFormResponsesSubscription,
  FillRecordTemplateApplyFormResponsesSubscriptionVariables,
  GetRecordTemplateSettingsQuery,
  GetRecordTemplateSettingsQueryVariables,
  GetRecordTemplateExpirationAutomationsQueryVariables,
  GetRecordTemplateExpirationAutomationsQuery,
  GenerateApplicationFormMutation,
  GenerateApplicationFormMutationVariables,
  GetGenerateApplicationFormUploadUrlMutation,
  GetGenerateApplicationFormUploadUrlMutationVariables,
} from 'types/graphql'

import { useMyOrgId } from 'src/layouts/AppStateContextLayout/utils'
import { monitoring } from 'src/utils/monitoring'
import { QueryKey, UseQueryOptions, useMutation, useQuery } from 'src/utils/queryClient'

const GetRecordTemplate = gql`
  query RecordTemplateQuery($recordTemplateUuid: String!) {
    recordTemplate(uuid: $recordTemplateUuid) {
      ...ApplyFormRecordTemplateFragment
    }
  }
`
export const useRecordTemplateQuery = (args: {
  recordTemplateUuid: string | null | undefined
  enabled?: boolean
}) => {
  return useQuery<RecordTemplateQuery, RecordTemplateQueryVariables>({
    queryDocument: GetRecordTemplate,
    queryKey: [QueryKey.RecordTemplate, args.recordTemplateUuid],
    variables: {
      recordTemplateUuid: args.recordTemplateUuid ?? '',
    },
    enabled: !!args.recordTemplateUuid && (!isNil(args.enabled) ? args.enabled : true),
  })
}

const GetRecordTemplates = gql`
  query GetRecordTemplatesQuery($input: RecordTemplatesInput) {
    recordTemplates(input: $input) {
      ...RecordTemplateOptionFragment
    }
  }
`
export const useRecordTemplatesQuery = (input?: RecordTemplatesInput) => {
  const orgId = useMyOrgId()
  const resolvedInput: RecordTemplatesInput | undefined = useMemo(
    () => ({
      ...input,
      organizationId: input?.organizationId ?? orgId,
    }),
    [input, orgId]
  )
  const result = useQuery<GetRecordTemplatesQuery, GetRecordTemplatesQueryVariables>({
    queryKey: [QueryKey.RecordTemplates, resolvedInput?.organizationId],
    queryDocument: GetRecordTemplates,
    variables: {
      input: resolvedInput,
    },
  })
  const recordTemplates = useMemo(
    () => result.data?.recordTemplates ?? [],
    [result.data?.recordTemplates]
  )
  return {
    ...omit(result, 'data'),
    recordTemplates,
  }
}

const GetWorkflowTemplates = gql`
  query WorkflowTemplatesQuery($recordTemplateId: Int!) {
    recordTemplateById(id: $recordTemplateId) {
      workflowTemplates {
        ...WorkflowTemplateOptionFragment
      }
    }
  }
`

export const useWorkflowTemplatesQuery = (
  args: {
    recordTemplateId: number | undefined
  },
  options?: UseQueryOptions<WorkflowTemplatesQuery, WorkflowTemplatesQueryVariables>
) => {
  const result = useQuery<WorkflowTemplatesQuery, WorkflowTemplatesQueryVariables>({
    ...options,
    queryKey: [QueryKey.WorkflowTemplates, args.recordTemplateId],
    queryDocument: GetWorkflowTemplates,
    variables: {
      recordTemplateId: args.recordTemplateId ?? -1,
    },
    enabled: !!args.recordTemplateId && (isNil(options?.enabled) || options.enabled),
  })

  const workflowTemplates = useMemo(
    () => result.data?.recordTemplateById?.workflowTemplates ?? [],
    [result.data?.recordTemplateById?.workflowTemplates]
  )
  return {
    ...omit(result, 'data'),
    workflowTemplates,
  }
}

const GetRecordTaskTemplateGroupOptionsForRecordTemplate = gql`
  query GetRecordTaskTemplateGroupOptionsForRecordTemplateQuery($id: Int!) {
    recordTemplateById(id: $id) {
      workflowTemplates {
        recordTaskTemplateGroups {
          ...RecordTaskTemplateGroupOptionFragment
        }
      }
    }
  }
`

export const useRecordTaskTemplateGroupOptionsForRecordTemplateQuery = (args: {
  recordTemplateId: number | null | undefined
}) => {
  return useQuery<
    GetRecordTaskTemplateGroupOptionsForRecordTemplateQuery,
    GetRecordTaskTemplateGroupOptionsForRecordTemplateQueryVariables
  >({
    queryKey: [QueryKey.RecordTaskTemplateGroupOptionsForRecordTemplate, args.recordTemplateId],
    queryDocument: GetRecordTaskTemplateGroupOptionsForRecordTemplate,
    variables: { id: args.recordTemplateId ?? -1 },
    enabled: !!args.recordTemplateId,
  })
}

const RecordTemplatesUserHasAccessTo = gql`
  query RecordTemplatesUserHasAccessToQuery($organizationId: Int) {
    recordTypesUserHasAccessTo(organizationId: $organizationId) {
      id
      name
      activeRecordTemplatesForMe {
        id
        name
        prefix
        recordTypeId
      }
    }
  }
`
export const useRecordTemplatesUserHasAccessToQuery = () => {
  const organizationId = useMyOrgId()
  return useQuery<
    RecordTemplatesUserHasAccessToQuery,
    RecordTemplatesUserHasAccessToQueryVariables
  >({
    queryKey: [QueryKey.RecordTemplatesUserHasAccessTo, organizationId],
    queryDocument: RecordTemplatesUserHasAccessTo,
    variables: { organizationId },
    enabled: !!organizationId,
  })
}

const Duplicate = gql`
  mutation DuplicateRecordTemplateMutation($id: Int!) {
    duplicateRecordTemplate(id: $id) {
      id
    }
  }
`

export const useDuplicateRecordTemplateMutation = () => {
  return useMutation<DuplicateRecordTemplateMutation, DuplicateRecordTemplateMutationVariables>({
    mutationDocument: Duplicate,
  })
}

const Create = gql`
  mutation CreateRecordTemplateMutation($recordTypeId: Int!) {
    createRecordTemplate(recordTypeId: $recordTypeId) {
      uuid
    }
  }
`
export const useCreateRecordTemplateMutation = () => {
  return useMutation<CreateRecordTemplateMutation, CreateRecordTemplateMutationVariables>({
    mutationDocument: Create,
  })
}

const Update = gql`
  mutation UpdateRecordTemplateMutation($id: Int!, $input: UpdateRecordTemplateInput!) {
    updateRecordTemplate(id: $id, input: $input) {
      id
    }
  }
`
export const useUpdateRecordTemplateMutation = () => {
  return useMutation<UpdateRecordTemplateMutation, UpdateRecordTemplateMutationVariables>({
    mutationDocument: Update,
  })
}

const LinkFieldGroupOnRecordSettingsToRecordTemplate = gql`
  mutation LinkFieldGroupOnRecordSettingsToRecordTemplateMutation(
    $id: Int!
    $fieldGroupOnRecordSettingsId: Int!
  ) {
    linkFieldGroupOnRecordSettingsToRecordTemplate(
      id: $id
      fieldGroupOnRecordSettingsId: $fieldGroupOnRecordSettingsId
    ) {
      id
    }
  }
`
export const useLinkFieldGroupOnRecordSettingsToRecordTemplateMutation = () => {
  return useMutation<
    LinkFieldGroupOnRecordSettingsToRecordTemplateMutation,
    LinkFieldGroupOnRecordSettingsToRecordTemplateMutationVariables
  >({
    mutationDocument: LinkFieldGroupOnRecordSettingsToRecordTemplate,
  })
}

const Archive = gql`
  mutation ArchiveRecordTemplateMutation($id: Int!) {
    archiveRecordTemplate(id: $id) {
      id
    }
  }
`
export const useArchiveRecordTemplateMutation = () => {
  return useMutation<ArchiveRecordTemplateMutation, ArchiveRecordTemplateMutationVariables>({
    mutationDocument: Archive,
  })
}

const Activate = gql`
  mutation ActivateRecordTemplateMutation($id: Int!) {
    activateRecordTemplate(id: $id) {
      id
    }
  }
`
export const useActivateRecordTemplateMutation = () => {
  return useMutation<ActivateRecordTemplateMutation, ActivateRecordTemplateMutationVariables>({
    mutationDocument: Activate,
  })
}

const FillRecordTemplateApplyForm = gql`
  mutation FillRecordTemplateApplyFormMutation(
    $id: Int!
    $input: FillRecordTemplateApplyFormInput!
  ) {
    fillRecordTemplateApplyForm(id: $id, input: $input) {
      ...FillRecordTemplateApplyFormFieldFragment
    }
  }
`

export const useFillRecordTemplateApplyFormMutation = () => {
  return useMutation<
    FillRecordTemplateApplyFormMutation,
    FillRecordTemplateApplyFormMutationVariables
  >({
    mutationDocument: FillRecordTemplateApplyForm,
  })
}

const FillRecordTemplateApplyFormResponses = gql`
  subscription FillRecordTemplateApplyFormResponsesSubscription($sessionId: String!) {
    fillRecordTemplateApplyFormResponses(sessionId: $sessionId) {
      deltaFields {
        ...FillRecordTemplateApplyFormFieldFragment
      }
      done
    }
  }
`

type UseSubscribeToFillRecordTemplateApplyFormResponsesArgs = {
  sessionId: string
  onResponseEvent: (
    event: FillRecordTemplateApplyFormResponsesSubscription['fillRecordTemplateApplyFormResponses']
  ) => void | Promise<void>
}

export const useSubscribeToFillRecordTemplateApplyFormResponses = (
  args: UseSubscribeToFillRecordTemplateApplyFormResponsesArgs
) => {
  const { sessionId, onResponseEvent } = args
  return useSubscription<
    FillRecordTemplateApplyFormResponsesSubscription,
    FillRecordTemplateApplyFormResponsesSubscriptionVariables
  >(FillRecordTemplateApplyFormResponses, {
    variables: {
      sessionId,
    },
    onData: async ({ data }) => {
      const event = data?.data?.fillRecordTemplateApplyFormResponses
      if (event) {
        await onResponseEvent(event)
      }
    },
    onError: (e) => {
      monitoring.logError(e, { source: 'recordTemplates subscription' })
    },
    shouldResubscribe: true,
  })
}

const GetRecordTemplateSettings = gql`
  query GetRecordTemplateSettingsQuery($recordTemplateId: Int!) {
    recordTemplateById(id: $recordTemplateId) {
      id
      recordSettingsDerived {
        ...OverridableRecordSettingsFragment
      }
      recordSettingsDerivedSources {
        ...OverridableRecordSettingSourcesFragment
      }
    }
  }
`

export const useRecordTemplateSettingsQuery = (
  recordTemplateId: number,
  options?: UseQueryOptions<GetRecordTemplateSettingsQuery, GetRecordTemplateSettingsQueryVariables>
) => {
  const { data, ...rest } = useQuery<
    GetRecordTemplateSettingsQuery,
    GetRecordTemplateSettingsQueryVariables
  >({
    queryKey: [QueryKey.RecordTemplateSettings, recordTemplateId],
    queryDocument: GetRecordTemplateSettings,
    variables: {
      recordTemplateId,
    },
    ...options,
  })

  const recordSettings = useMemo(
    () => data?.recordTemplateById?.recordSettingsDerived ?? {},
    [data?.recordTemplateById?.recordSettingsDerived]
  )
  const recordSettingsSources = useMemo(
    () => data?.recordTemplateById?.recordSettingsDerivedSources ?? {},
    [data?.recordTemplateById?.recordSettingsDerivedSources]
  )

  return {
    recordSettings,
    recordSettingsSources,
    ...rest,
  }
}

const GetRecordTemplateExpirationAutomations = gql`
  query GetRecordTemplateExpirationAutomationsQuery($recordTemplateId: Int!) {
    recordTemplateById(id: $recordTemplateId) {
      id
      automationTemplates {
        ...AutomationTemplateFragment
      }
      recordType {
        id
        renewalsMigrationStartedAt
        renewalsRollbackStartedAt
        recordTemplates {
          id
          archivedAt
          renewalPolicy {
            id
          }
        }
        automationTemplates {
          ...AutomationTemplateFragment
        }
      }
    }
  }
`

export const useRecordTemplateExpirationAutomationsQuery = (
  recordTemplateId: number,
  options?: UseQueryOptions<
    GetRecordTemplateExpirationAutomationsQuery,
    GetRecordTemplateExpirationAutomationsQueryVariables
  >
) => {
  const { data, ...rest } = useQuery<
    GetRecordTemplateExpirationAutomationsQuery,
    GetRecordTemplateExpirationAutomationsQueryVariables
  >({
    queryKey: [QueryKey.RecordTemplateExpirationAutomations, recordTemplateId],
    queryDocument: GetRecordTemplateExpirationAutomations,
    variables: {
      recordTemplateId,
    },
    ...options,
  })

  const automationTemplates = useMemo(
    () => data?.recordTemplateById?.automationTemplates ?? [],
    [data?.recordTemplateById?.automationTemplates]
  )

  // Filter for expiration automation templates only
  const expirationAutomationTemplates = useMemo(
    () => automationTemplates.filter((at) => at.isExpirationAutomation),
    [automationTemplates]
  )

  // Get record type's expiration automation templates
  const recordTypeAutomationTemplates = useMemo(
    () => data?.recordTemplateById?.recordType?.automationTemplates ?? [],
    [data?.recordTemplateById?.recordType?.automationTemplates]
  )

  const recordType = useMemo(
    () => data?.recordTemplateById?.recordType,
    [data?.recordTemplateById?.recordType]
  )

  return {
    automationTemplates,
    expirationAutomationTemplates,
    recordType,
    recordTypeAutomationTemplates,
    ...rest,
  }
}

const FillFormFieldsFromGIS = gql`
  mutation FillFormFieldsFromGISMutation($input: FillFormFieldsFromGISInput!) {
    fillFormFieldsFromGIS(input: $input) {
      ...FillRecordTemplateApplyFormFieldFragment
    }
  }
`

export const useFillFormFieldsFromGISMutation = () => {
  return useMutation<FillFormFieldsFromGISMutation, FillFormFieldsFromGISMutationVariables>({
    mutationDocument: FillFormFieldsFromGIS,
  })
}

const GetGenerateApplicationFormUploadUrl = gql`
  mutation GetGenerateApplicationFormUploadUrlMutation(
    $input: GetGenerateApplicationFormUploadUrlInput!
  ) {
    getGenerateApplicationFormUploadUrl(input: $input) {
      uploadUrl
      fileKey
    }
  }
`

export const useGetGenerateApplicationFormUploadUrlMutation = () => {
  return useMutation<
    GetGenerateApplicationFormUploadUrlMutation,
    GetGenerateApplicationFormUploadUrlMutationVariables
  >({
    mutationDocument: GetGenerateApplicationFormUploadUrl,
  })
}

const GenerateApplicationFormDoc = gql`
  mutation GenerateApplicationFormMutation($input: GenerateApplicationFormInput!) {
    generateApplicationForm(input: $input)
  }
`

export const useGenerateApplicationFormMutation = () => {
  return useMutation<GenerateApplicationFormMutation, GenerateApplicationFormMutationVariables>({
    mutationDocument: GenerateApplicationFormDoc,
  })
}
